/**
 * TanStack Query hooks для pipeline endpoints (Phase 3).
 *
 * - useStartPipeline / usePause / useResume / useCancel
 * - usePipelineStatus (auto-refetch пока RUNNING)
 * - usePipelineRows (превью первых N строк)
 * - useLatestPipelineByDataset
 * - useExportPipeline
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import api from '@/lib/api';

// === Types ===

export type PipelineStatus =
  | 'queued'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type TierKey = 'economy' | 'optimal' | 'max';

export interface PipelineRun {
  id: string;
  dataset_id: string;
  status: PipelineStatus;
  is_test_run: boolean;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  error_rows: number;
  estimated_credits: string;
  spent_credits: string;
  reserved_credits: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  // Fix 2026-09-19 (run visibility, TZ A2): run mode and actual scope from
  // pipeline_config (None for runs created before the fix).
  scope_row_ids?: string[] | null;
  overwrite_existing?: boolean | null;
  search_trigger?: string | null;
}

export interface PipelineRow {
  id: string;
  row_index: number;
  status: string;
  generated_content: Record<string, unknown> | null;
  enriched_data: Record<string, unknown> | null;
  // Block M1a (TZ 2026-07-29): matching writes into mapped_data (named layer
  // per ADR-0002); backend already exposes it via /content/row/{id}.
  mapped_data?: Record<string, unknown> | null;
  raw_data: Record<string, unknown>;
  error_message: string | null;
  spent_credits: string;
}

export interface PipelineStartBody {
  dataset_id: string;
  tier_key: TierKey;
  module_keys: string[];
  output_language: string;
  temperature?: number;
  max_tokens?: number;
  selected_columns?: string[] | null;
  is_test_run?: boolean;
  test_run_limit?: number;
  // Fix §7 (2026-09-17): false (default) — строки с готовым результатом
  // search_v2 пропускаются; true — перезаписать весь датасет.
  overwrite_existing?: boolean;
  // Block S1 (Search v2 foundation)
  priority_domains?: string[];
  // Block 2026-08-02 (Generation foundation): per-content-type settings,
  // persisted into pipeline_config. Sent for all configured types (settings
  // are captured even for types whose runtime is "coming soon").
  content_type_configs?: Record<string, Record<string, unknown>>;
}

// === Queries ===

export function useLatestPipelineByDataset(
  datasetId: string | null,
  options?: Partial<UseQueryOptions<PipelineRun | null>>,
) {
  return useQuery<PipelineRun | null>({
    queryKey: ['pipeline', 'latest-by-dataset', datasetId],
    queryFn: async () => {
      if (!datasetId) return null;
      const r = await api.get<PipelineRun | null>(
        `/pipeline/by-dataset/${datasetId}/latest`,
      );
      return r.data;
    },
    enabled: Boolean(datasetId),
    ...options,
  });
}

export function usePipelineStatus(
  pipelineId: string | null,
  options?: Partial<UseQueryOptions<PipelineRun>>,
) {
  return useQuery<PipelineRun>({
    queryKey: ['pipeline', pipelineId],
    queryFn: async () =>
      (await api.get<PipelineRun>(`/pipeline/${pipelineId}`)).data,
    enabled: Boolean(pipelineId),
    refetchInterval: (q) => {
      const data = q.state.data as PipelineRun | undefined;
      if (!data) return false;
      if (
        data.status === 'running' ||
        data.status === 'queued' ||
        data.status === 'paused'
      ) {
        return 2_000;
      }
      return false;
    },
    ...options,
  });
}

export function usePipelineRows(
  pipelineId: string | null,
  limit: number = 100,
  options?: Partial<UseQueryOptions<{ pipeline_id: string; total: number; rows: PipelineRow[] }>>,
  includeUnprocessed: boolean = false,
) {
  return useQuery({
    queryKey: ['pipeline', pipelineId, 'rows', limit, includeUnprocessed],
    queryFn: async () =>
      (
        await api.get<{
          pipeline_id: string;
          total: number;
          rows: PipelineRow[];
        }>(`/content/pipeline/${pipelineId}/rows`, {
          params: { limit, include_unprocessed: includeUnprocessed },
        })
      ).data,
    enabled: Boolean(pipelineId),
    // FIX 2026-08-31: при возврате на страницу Search — всегда refetch,
    // даже если в кэше React Query есть данные (staleTime=30s).
    // Без этого результаты могли «исчезать», показывая stale-кэш,
    // пока не пройдёт 30s.
    refetchOnMount: 'always',
    // M1 (2026-09-10): опрос строк каждые 2s, пока идёт обработка —
    // строки появляются по мере завершения (endpoints отдают только
    // не-pending строки). Коллер может форсировать интервал через options.
    refetchInterval: (q) => {
      const data = q.state.data as
        | { total: number; rows: PipelineRow[] }
        | undefined;
      if (!data) return false;
      const activeRow = data.rows.some(
        (r) => r.status === 'processing' || r.status === 'retry_waiting',
      );
      if (activeRow || data.rows.length < data.total) return 2_000;
      return false;
    },
    ...options,
  });
}

// === Mutations ===

export function useStartPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PipelineStartBody) =>
      (await api.post<PipelineRun>('/pipeline/start', body)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.setQueryData(['pipeline', data.id], data);
      qc.invalidateQueries({
        queryKey: ['pipeline', 'latest-by-dataset', data.dataset_id],
      });
    },
  });
}

export function usePausePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId: string) =>
      (await api.post<PipelineRun>(`/pipeline/${pipelineId}/pause`)).data,
    onSuccess: (data) => {
      qc.setQueryData(['pipeline', data.id], data);
    },
  });
}

export function useResumePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId: string) =>
      (await api.post<PipelineRun>(`/pipeline/${pipelineId}/resume`)).data,
    onSuccess: (data) => {
      qc.setQueryData(['pipeline', data.id], data);
    },
  });
}

export function useCancelPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId: string) =>
      (await api.post<PipelineRun>(`/pipeline/${pipelineId}/cancel`)).data,
    onSuccess: (data) => {
      qc.setQueryData(['pipeline', data.id], data);
    },
  });
}

export function useExportPipeline() {
  return useMutation({
    mutationFn: async ({
      pipelineId,
      format = 'csv',
    }: {
      pipelineId: string;
      format?: 'csv' | 'json';
    }) => {
      const r = await api.get(`/content/pipeline/${pipelineId}/export`, {
        params: { format },
        responseType: 'blob',
      });
      return r.data as Blob;
    },
  });
}
